"use client";

import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import pages from "@/config/pages";
import useLanguage from "@/hooks/useLanguage";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import cacheKeys from "@/config/cache-keys";
import AppService from "@/services/AppService";
import { useQuery } from "@tanstack/react-query";
import { MenuIcon } from "lucide-react";

export default function NavbarClient() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const { t } = useTranslation();

	const appService = new AppService();

	const query = useQuery({
		queryKey: cacheKeys.CLIENT_DATA,
		queryFn: async () => appService.getClientData(id as string),
		enabled: !!id,
	});
	const display_name = query.data?.Result?.display_name || "Finner ikke navn";
	const group_name = query.data?.Result?.group_name || "Finner ikke gruppe";

	return (
		<div className="fixed top-4 left-0 w-full z-50">
			<nav className="relative mx-auto w-full max-w-7xl bg-white/20 backdrop-blur-md shadow-lg flex items-center h-16 px-2 sm:px-4 md:px-8 md:rounded transition-all duration-300 ease-in-out">
				{/* MOBILE NAVBAR (logo + display_name + hamburger) */}
				<div className="flex md:hidden w-full justify-between items-center">
					{/* Logo and display_name on the left */}
					<div className="flex items-center space-x-1 flex-wrap min-w-0">
						<img
							className="w-auto h-10 sm:h-12 md:h-16 hover:cursor-pointer hover:scale-105 transition-transform"
							src="/assets/logo/logo-white.svg"
							alt="Howden Logo"
							onClick={() => {
								window.scrollTo(0, 0);
								navigate(pages.Home(id));
							}}
						/>
						<span className="text-white font-header text-sm sm:text-base font-medium">
							{display_name} - {group_name}
						</span>
					</div>
					{/* Hamburger menu on the right */}
					<Button
						size="icon"
						variant="ghost"
						className="text-white hover:text-teal-700 shrink-0"
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						aria-label={t("navbar.menu")}
					>
						<MenuIcon className="h-6 w-6" />
					</Button>
				</div>

				{/* DESKTOP NAVBAR */}
				<div className="hidden md:flex w-full justify-between items-center">
					{/* Howden Logo and display_name */}
					<div className="flex-1 flex items-center justify-start space-x-4">
						<div className="flex items-center space-x-2 flex-wrap">
							<Tooltip>
								<TooltipTrigger
									onClick={() => {
										window.scrollTo(0, 0);
										navigate(pages.Home(id));
									}}
								>
									<img
										className="h-16 w-auto hover:scale-105 transition-transform"
										src="/assets/logo/logo-white.svg"
										alt="Howden Logo"
									/>
								</TooltipTrigger>
								<TooltipContent side="bottom">
									{t("navbar.home")}
								</TooltipContent>
							</Tooltip>
							<span className="text-white font-header text-lg font-medium">
								{display_name} - {group_name}
							</span>
						</div>
					</div>
					{/* RIGHT SECTION */}
					<div className="flex-1 flex justify-end space-x-4 md:space-x-6">
						<Tooltip>
							<TooltipTrigger
								className="text-white hover:text-moss-green transition-colors font-header text-sm md:text-base"
								onClick={() => {
									window.scrollTo(0, 0);
									navigate(pages.ClientPortalCalculator(id));
								}}
							>
								{t("navbar.calculator")}
							</TooltipTrigger>
							<TooltipContent side="bottom">
								{t("navbar.calculator-tooltip")}
							</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger
								className="text-white hover:text-moss-green transition-colors font-header text-sm md:text-base"
								onClick={() => {
									window.scrollTo(0, 0);
									navigate(pages.ClientPortalDocuments(id));
								}}
							>
								{t("navbar.documents")}
							</TooltipTrigger>
							<TooltipContent side="bottom">
								{t("navbar.documents-tooltip")}
							</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									size="icon"
									variant="ghost"
									className="text-white hover:text-teal-700"
								>
									<a
										href="https://norskpensjon.no/"
										target="_blank"
										rel="noopener noreferrer"
									>
										<img
											className="h-5 w-auto"
											src="/assets/clientportal/norsk-pensjon-icon.png"
											alt="Norsk Pensjon"
										/>
									</a>
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">
								{t("navbar.norskpensjon-tooltip")}
							</TooltipContent>
						</Tooltip>
						<LanguageSelect />
					</div>
				</div>
			</nav>

			{/* MOBILE NAVBAR MENU */}
			<div
				className={`md:hidden absolute top-[4rem] left-0 w-full transition-all duration-300 ease-in-out ${
					mobileMenuOpen
						? "opacity-100 translate-y-0"
						: "opacity-0 -translate-y-2 pointer-events-none"
				}`}
			>
				<div className="bg-white/20 backdrop-blur-md shadow-lg px-4 sm:px-6 py-4 space-y-3">
					<LanguageSelect />
					<button
						className="block py-2 font-header text-base text-white hover:text-moss-green transition-colors"
						onClick={() => {
							window.scrollTo(0, 0);
							setMobileMenuOpen(false);
							navigate(pages.ClientPortalCalculator(id));
						}}
					>
						{t("navbar.calculator")}
					</button>

					<button
						className="block py-2 font-header text-base text-white hover:text-moss-green transition-colors"
						onClick={() => {
							window.scrollTo(0, 0);
							setMobileMenuOpen(false);
							navigate(pages.ClientPortalDocuments(id));
						}}
					>
						{t("navbar.documents")}
					</button>
					<Button
						size="icon"
						variant="ghost"
						className="text-soft-white hover:text-teal-700"
						onClick={() => setMobileMenuOpen(false)}
					>
						<a
							href="https://norskpensjon.no/"
							target="_blank"
							rel="noopener noreferrer"
						>
							<img
								className="h-5 w-auto"
								src="/assets/clientportal/norsk-pensjon-icon.png"
								alt="Norsk Pensjon"
							/>
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}

function LanguageSelect() {
	const { currentLanguage, setLanguage } = useLanguage();
	const { t } = useTranslation();

	const handleLanguageToggle = () => {
		setLanguage(currentLanguage === "en" ? "nb-NO" : "en");
	};

	return (
		<Tooltip>
			<TooltipTrigger
				className="text-sm cursor-pointer text-white hover:text-moss-green transition-colors font-header"
				onClick={handleLanguageToggle}
			>
				{currentLanguage === "en" ? (
					<span className="underline">{t("language.short.en")}</span>
				) : (
					<span>{t("language.short.en")}</span>
				)}
				<span>/</span>
				{currentLanguage === "nb-NO" ? (
					<span className="underline">{t("language.short.no")}</span>
				) : (
					<span>{t("language.short.no")}</span>
				)}
			</TooltipTrigger>
			<TooltipContent>{t("navbar.change-language")}</TooltipContent>
		</Tooltip>
	);
}
